import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { SubscriptionPlan } from 'services/@types';
import SubscriptionPlanService from 'services/subscriptionPlan.api';

export interface SubscriptionPlanState {
  plans: SubscriptionPlan[];
  plan: SubscriptionPlan | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalResults: number;
  };
}

const initialState: SubscriptionPlanState = {
  plans: [],
  plan: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  },
};

export const fetchSubscriptionPlans = createAsyncThunk(
  'subscriptionPlan/fetchAll',
  async (params: Record<string, any>, { rejectWithValue }) => {
    try {
      return await SubscriptionPlanService.getSubscriptionPlans(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createSubscriptionPlan = createAsyncThunk(
  'subscriptionPlan/create',
  async (plan: Partial<SubscriptionPlan>, { rejectWithValue }) => {
    try {
      return await SubscriptionPlanService.createSubscriptionPlan(plan);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateSubscriptionPlan = createAsyncThunk(
  'subscriptionPlan/update',
  async (
    { id, updates }: { id: string; updates: Partial<SubscriptionPlan> },
    { rejectWithValue },
  ) => {
    try {
      return await SubscriptionPlanService.updateSubscriptionPlan(id, updates);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const subscriptionPlanSlice = createSlice({
  name: 'subscriptionPlan',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload.results;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          totalPages: action.payload.totalPages,
          totalResults: action.payload.totalResults,
        };
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createSubscriptionPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubscriptionPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.plan = action.payload;
      })
      .addCase(createSubscriptionPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateSubscriptionPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubscriptionPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.plan = action.payload;
      })
      .addCase(updateSubscriptionPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default subscriptionPlanSlice.reducer;
